import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { organizationService, programService } from '@/services/api';

/**
 * CompleteSignup page shown after registration.
 * Polls for organization and program assignments before redirecting to home.
 * This is an unauthenticated route that requires auth token to be present.
 */
export const CompleteSignupPage = () => {
  const navigate = useNavigate();
  const [firstOrgId, setFirstOrgId] = useState<string | null>(null);
  const pollingAttemptsRef = useRef(0);
  const maxAttempts = 60; // 60 attempts * 2 seconds = 2 minutes max

  // Poll for organizations every 2 seconds
  const { data: orgsData } = useQuery({
    queryKey: ['signup-organizations'],
    queryFn: async () => {
      pollingAttemptsRef.current += 1;
      const response = await organizationService.getCurrentUserOrganizations({});
      return response.organizations || [];
    },
    refetchInterval: 3_000, // Poll every 3 seconds
    retry: false, // Don't retry on failure, just keep polling
    staleTime: 0, // Always consider data stale to ensure fresh fetches
  });

  // Extract first org ID when orgs are available
  useEffect(() => {
    if (orgsData && orgsData.length > 0 && !firstOrgId) {
      setFirstOrgId(orgsData[0].id || null);
    }
  }, [orgsData, firstOrgId]);

  // Poll for programs only when we have an organization
  const { data: programsData } = useQuery({
    queryKey: ['signup-programs', firstOrgId],
    queryFn: async () => {
      if (!firstOrgId) return [];
      const response = await programService.getCurrentUserPrograms({
        organization_id: firstOrgId,
      });
      return response.programs || [];
    },
    enabled: !!firstOrgId, // Only run when we have an org ID
    refetchInterval: 3_000, // Poll every 3 seconds
    retry: false,
    staleTime: 0,
  });

  const hasOrg = (orgsData?.length || 0) > 0;
  const hasProgram = (programsData?.length || 0) > 0;

  useEffect(() => {
    // If we have both organization and program, redirect to home
    if (hasOrg && hasProgram) {
      // Small delay to show success state
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 500);
      return;
    }

    // If we've exceeded max attempts, redirect anyway (fail-safe)
    if (pollingAttemptsRef.current >= maxAttempts) {
      console.warn('Max polling attempts reached, redirecting to home');
      navigate('/', { replace: true });
      return;
    }
  }, [hasOrg, hasProgram, maxAttempts, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted">
      <div className="text-center">
        <div className="rounded-full h-8 w-8 mx-auto">
          <img
            src="/images/company/hms-header-logo.png"
            alt="100ms logo"
            className="animate-bounce"
          />
        </div>
        <p className="mt-3 text-sm text-muted-foreground">Getting things ready…</p>
      </div>
    </div>
  );
};
